// 연동 이력 컴포넌트 (모바일 최적화 + 페이징)
// src/pages/admin/components/draw/SyncLogList.tsx

import type { SyncLog } from '@/pages/admin/components/draw/types';
import Pagination from '@/components/base/Pagination';

interface SyncLogListProps {
  syncLogs: SyncLog[];
  logsLoading: boolean;
  currentPage: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'success':
      return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">성공</span>;
    case 'partial':
      return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700">일부 성공</span>;
    case 'failed':
      return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">실패</span>;
    case 'running':
      return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">진행 중</span>;
    case 'cancelled':
      return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-background-200 text-foreground-500">중단됨</span>;
    default:
      return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-background-200 text-foreground-500">{status}</span>;
  }
}

function formatDate(d: string | null) {
  if (!d) return '-';
  return new Date(d).toLocaleString('ko-KR');
}

function triggeredByLabel(t: string) {
  if (t === 'auto') return '자동';
  if (t === 'cron') return '스케줄';
  return '수동';
}

export default function SyncLogList({ syncLogs, logsLoading, currentPage, totalCount, pageSize, onPageChange }: SyncLogListProps) {
  return (
    <div>
      <h4 className="font-heading text-sm text-foreground-700 mb-4 flex items-center gap-2">
        <i className="ri-history-line text-base"></i>
        연동 이력
      </h4>
      {logsLoading ? (
        <div className="text-center py-8">
          <div className="w-6 h-6 mx-auto mb-2 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-foreground-400 text-xs">불러오는 중...</p>
        </div>
      ) : syncLogs.length === 0 ? (
        <p className="text-foreground-400 text-xs text-center py-8 bg-background-50 rounded-2xl border border-background-200">
          아직 연동 이력이 없습니다.
        </p>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto bg-background-50 rounded-2xl border border-background-200">
            <table className="w-full text-sm min-w-[600px]">
              <thead>
                <tr className="border-b border-background-200">
                  <th className="text-left py-3 px-4 text-foreground-400 font-medium">시작</th>
                  <th className="text-left py-3 px-4 text-foreground-400 font-medium">범위</th>
                  <th className="text-left py-3 px-4 text-foreground-400 font-medium">시도</th>
                  <th className="text-left py-3 px-4 text-foreground-400 font-medium">성공</th>
                  <th className="text-left py-3 px-4 text-foreground-400 font-medium">실패</th>
                  <th className="text-left py-3 px-4 text-foreground-400 font-medium">상태</th>
                  <th className="text-left py-3 px-4 text-foreground-400 font-medium">방식</th>
                  <th className="text-left py-3 px-4 text-foreground-400 font-medium">완료</th>
                </tr>
              </thead>
              <tbody>
                {syncLogs.map((log) => (
                  <tr key={log.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                    <td className="py-2.5 px-4 text-foreground-500 text-xs whitespace-nowrap">
                      {formatDate(log.created_at)}
                    </td>
                    <td className="py-2.5 px-4 text-foreground-800 font-medium whitespace-nowrap">
                      {log.start_round}~{log.end_round}회
                    </td>
                    <td className="py-2.5 px-4 text-foreground-600">{log.total_attempted}</td>
                    <td className="py-2.5 px-4 text-secondary-600 font-medium">{log.inserted}</td>
                    <td className="py-2.5 px-4">
                      {log.failed > 0 ? (
                        <span className="text-accent-600 font-medium" title={log.failed_rounds?.join(', ')}>
                          {log.failed}
                        </span>
                      ) : (
                        <span className="text-foreground-400">0</span>
                      )}
                    </td>
                    <td className="py-2.5 px-4">{getStatusBadge(log.status)}</td>
                    <td className="py-2.5 px-4">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        log.triggered_by === 'auto'
                          ? 'bg-secondary-50 text-secondary-600'
                          : 'bg-background-200 text-foreground-500'
                      }`}>
                        {triggeredByLabel(log.triggered_by)}
                      </span>
                    </td>
                    <td className="py-2.5 px-4 text-foreground-400 text-xs whitespace-nowrap">
                      {log.completed_at ? formatDate(log.completed_at) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {syncLogs.map((log) => (
              <div key={log.id} className="bg-background-50 rounded-xl border border-background-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground-800">{log.start_round}~{log.end_round}회</span>
                  {getStatusBadge(log.status)}
                </div>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <div className="text-center">
                    <p className="text-xs text-foreground-400">시도</p>
                    <p className="text-sm text-foreground-700">{log.total_attempted}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-foreground-400">성공</p>
                    <p className="text-sm text-secondary-600 font-medium">{log.inserted}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-foreground-400">실패</p>
                    <p className="text-sm text-accent-600 font-medium">{log.failed}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-foreground-400">
                  <span>{formatDate(log.created_at)}</span>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                    log.triggered_by === 'auto'
                      ? 'bg-secondary-50 text-secondary-600'
                      : 'bg-background-200 text-foreground-500'
                  }`}>
                    {triggeredByLabel(log.triggered_by)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      <Pagination
        currentPage={currentPage}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageChange={onPageChange}
      />
    </div>
  );
}